import React, { useState, useEffect, useRef } from 'react';
import { X, Trophy, Star, TrendingUp, Clock, ArrowUpCircle, Sparkles, SlidersHorizontal, Filter, ArrowUpDown, ChevronDown, Calendar, AlertTriangle, Zap, Moon, Sun, Briefcase, HeartHandshake, Crown } from 'lucide-react';
import {
  calculateProduction,
  BERRY_ICONS,
  INGREDIENT_ICONS,
  MAIN_SKILL_ICONS,
  NATURES,
  SUBSKILL_ABBREVIATIONS,
  getSkillColor,
  HYBRID_ROLES,
  formatSkillName
} from '../utils/gameData';
import { getEffectiveSkillLevel, getToxelEvolution } from '../utils/pokemonMaths';

const SUBSKILL_UPGRADES = {
  'Helping Speed S': ['Helping Speed S', 'Helping Speed M'],
  'Ingredient Finder S': ['Ingredient Finder S', 'Ingredient Finder M'],
  'Inventory Up S': ['Inventory Up S', 'Inventory Up M', 'Inventory Up L'],
  'Skill Trigger S': ['Skill Trigger S', 'Skill Trigger M'],
  'Skill Level Up S': ['Skill Level Up S', 'Skill Level Up M']
};

// NEW COMPONENT: Dynamic SVG Background Graph
const BackgroundGraph = ({ growthData, type, colorClass, currentLevel }) => {
    if (!growthData || growthData.length === 0) return null;
    
    // Add small padding to maxVal so the peak of the curve doesn't touch the very top edge 
    const rawMax = Math.max(...growthData.map(d => d[type]), 0.1);
    const maxVal = rawMax * 1.1; 
    
    const points = growthData.map(d => {
        const x = d.lvl; 
        const y = 100 - ((d[type] / maxVal) * 100);
        return `${x},${y}`;
    }).join(' ');

    const currentData = growthData.find(d => d.lvl === currentLevel) || growthData[growthData.length - 1];
    const currentY = 100 - ((currentData[type] / maxVal) * 100);

    return (
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className={`absolute inset-0 w-full h-full pointer-events-none z-0 ${colorClass}`}>
            {/* Fill under the curve */}
            <polygon points={`1,100 ${points} 100,100`} fill="currentColor" opacity="0.08" />
            {/* The curve itself */}
            <polyline points={points} fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.3" />
            {/* Current level indicator line */}
            <line x1={currentLevel} y1="0" x2={currentLevel} y2="100" stroke="currentColor" strokeWidth="0.5" strokeDasharray="2 2" opacity="0.4" />
            {/* Current level point dot */}
            <circle cx={currentLevel} cy={currentY} r="2" fill="currentColor" opacity="0.8" />
        </svg>
    );
};

export default function CompareModal({ selectedMons, pokedex, onClose, onRemove }) {
  
  // --- 1. HOOKS ---
  const [duration, setDuration] = useState(24);
  const [energyStrategy, setEnergyStrategy] = useState('maintained');
  const [inventoryMode, setInventoryMode] = useState('unlimited');
  const [showGodRolls, setShowGodRolls] = useState(false);
  const [enableMintsSeeds, setEnableMintsSeeds] = useState(false);
  
  const [useFinalEvolution, setUseFinalEvolution] = useState(false);
  const [gradingBasis] = useState('overall');
  const [sortMethod, setSortMethod] = useState('level');
  
  const [simLevels, setSimLevels] = useState({});
  const [selectedEvos, setSelectedEvos] = useState({});
  const [godRollIngredients, setGodRollIngredients] = useState({}); 
  
  const [useGlobalLevel, setUseGlobalLevel] = useState(false);
  const [globalLevel, setGlobalLevel] = useState(50);
  const [snapGlobalLevel, setSnapGlobalLevel] = useState(false);
  const [ingFilter, setIngFilter] = useState(new Set());
  const [isIngFilterOpen, setIsIngFilterOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [mintedMons, setMintedMons] = useState(new Set());
  const [seededSubskills, setSeededSubskills] = useState({});
  const [seededMainSkills, setSeededMainSkills] = useState({});
  
  const filterRef = useRef(null);
  const [expandedMobile, setExpandedMobile] = useState({
    active: false,
    berry: true,
    ing: true,
    skill: true,
    verdict: true
  });

  useEffect(() => {
    if (gradingBasis === 'berry') setSortMethod('berry');
    if (gradingBasis === 'ing') setSortMethod('ing');
    if (gradingBasis === 'skill') setSortMethod('skill');
  }, [gradingBasis]);
  
  useEffect(() => {
    function handleClickOutside(event) {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setIsIngFilterOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  if (!selectedMons || selectedMons.length === 0) return null;

  // --- 2. HELPERS ---
  const toggleIngFilter = (ing) => {
    const newSet = new Set(ingFilter);
    if (newSet.has(ing)) newSet.delete(ing);
    else newSet.add(ing);
    setIngFilter(newSet);
  };

  const toggleMint = (uuid) => {
    setMintedMons(prev => {
      const newSet = new Set(prev);
      if (newSet.has(uuid)) newSet.delete(uuid);
      else newSet.add(uuid);
      return newSet;
    });
  };

  const handleSubskillTap = (uuid, slotIndex, baseSubskill, currentSimulatedSubskills) => {
    const upgradePath = SUBSKILL_UPGRADES[baseSubskill];
    if (!upgradePath) return;

    const currentSkill = currentSimulatedSubskills[slotIndex];
    const currentIndex = upgradePath.indexOf(currentSkill);
    const nextIndex = (currentIndex + 1) % upgradePath.length;
    const nextSkill = upgradePath[nextIndex];

    // Prevent applying the upgrade if the target skill already exists in another slot
    const existsElsewhere = currentSimulatedSubskills.some((skill, idx) => idx !== slotIndex && skill === nextSkill);
    if (existsElsewhere) return;

    setSeededSubskills(prev => {
        const newSkills = [...currentSimulatedSubskills];
        newSkills[slotIndex] = nextSkill;
        return { ...prev, [uuid]: newSkills };
    });
  };

  const handleMainSkillTap = (uuid, currentBaseLevel, allSubskills, simLevel) => {
    const unlockedSubskills = allSubskills.filter((skill, i) => simLevel >= [10, 25, 50, 75, 100][i]);
    const currentEffective = getEffectiveSkillLevel(currentBaseLevel, unlockedSubskills);
    if (currentEffective >= 7) {
        setSeededMainSkills(prev => {
            const newSkills = { ...prev };
            delete newSkills[uuid];
            return newSkills;
        });
    } else {
        setSeededMainSkills(prev => ({ ...prev, [uuid]: currentBaseLevel + 1 }));
    }
  };

  const toggleMobileSection = (sec) => setExpandedMobile(prev => ({ ...prev, [sec]: !prev[sec] }));

  const getEvolutionOptions = (currentSpecies, monNature) => {
    if (!currentSpecies.family) return [];
    const familyMembers = Object.values(pokedex).filter(p => p.family === currentSpecies.family);

    if (currentSpecies.id === 'toxel' && monNature) {
        const targetSpecies = pokedex[getToxelEvolution(monNature)];
        return targetSpecies ? [targetSpecies] : [];
    }

    let finalStages = familyMembers.filter(p => p.final_evolution === p.id);
    if (finalStages.some(f => f.id === currentSpecies.id)) return [];

    return finalStages.sort((a, b) => a.name.localeCompare(b.name));
  };

  // --- 3. GOD ROLL GENERATOR ---
  const generateGodRoll = (baseMon, species, customUuid) => {
    const uuid = customUuid || `god-${species.id}`; 
    let godSubskills = [];
    let godNature = "Hardy";

    if (species.specialty === 'berry') {
        godSubskills = ["Berry Finding S", "Helping Bonus", "Helping Speed M", "Helping Speed S", "Skill Trigger M"];
        godNature = "Adamant"; 
    } else if (species.specialty === 'ingredient') {
        godSubskills = ["Ingredient Finder M", "Helping Bonus", "Helping Speed M", "Inventory Up L", "Ingredient Finder S"];
        godNature = "Quiet"; 
    } else if (species.specialty === 'skill') { 
        godSubskills = ["Skill Trigger M", "Skill Trigger S", "Helping Bonus", "Helping Speed M", "Berry Finding S"];
        godNature = "Sassy"; 
    } else {
        godSubskills = ["Berry Finding S", "Helping Bonus", "Helping Speed M", "Skill Trigger M", "Ingredient Finder M"];
        godNature = "Brave"; 
    }

    const activeIngredients = godRollIngredients[uuid] || baseMon.ingredients;

    return {
        ...baseMon,
        uuid: uuid,
        nickname: `Perfect Stats ${species.name}`,
        species_id: species.id, 
        level: 100,
        nature: godNature,
        subskills: godSubskills,
        is_god_roll: true,
        ingredients: activeIngredients
    };
  };

  const cycleGodRollIngredient = (uuid, species, level) => {
    const familyMember = selectedMons.find(m => pokedex[m.species_id].family === species.family);
    const currentIngs = godRollIngredients[uuid] || (familyMember ? familyMember.ingredients : null);
    
    if (!currentIngs) return;

    const pool = species.ingredientPools[level];
    if (!pool || pool.length <= 1) return;

    const currentName = currentIngs[level].name;
    const currentIndex = pool.findIndex(i => i.name === currentName);
    const nextIndex = (currentIndex + 1) % pool.length;
    const nextIng = pool[nextIndex];

    setGodRollIngredients(prev => ({
        ...prev,
        [uuid]: {
            ...currentIngs,
            [level]: nextIng
        }
    }));
  };

  // --- 4. DATA PREP ---
  let processedMons = [...selectedMons];

  if (showGodRolls) {
    const uniqueFamilies = new Set();
    const godRolls = [];
    
    selectedMons.forEach(mon => {
        const species = pokedex[mon.species_id];
        if (!species) return;

        if (!uniqueFamilies.has(species.family)) {
            uniqueFamilies.add(species.family);
            
            const uuid = `god-${species.family}`;
            let finalId = species.final_evolution;
            
            if (species.id === 'toxel') {
                let godNature = "Hardy";
                if (species.specialty === 'berry') godNature = "Adamant";
                else if (species.specialty === 'ingredient') godNature = "Quiet";
                else if (species.specialty === 'skill') godNature = "Sassy";
                else godNature = "Brave";
                finalId = getToxelEvolution(godNature);
            }

            const userChoiceId = selectedEvos[uuid];
            if (userChoiceId && pokedex[userChoiceId]) {
                finalId = userChoiceId;
            }

            if (!finalId || !pokedex[finalId]) finalId = species.id; 

            const finalSpecies = pokedex[finalId];
            if (finalSpecies) {
                const godRollMon = generateGodRoll(mon, finalSpecies, uuid);
                godRollMon.base_species_id = species.id;
                godRolls.push(godRollMon);
            }
        }
    });
    processedMons = [...processedMons, ...godRolls];
  }

  const rawData = processedMons.map(mon => {
    const effectiveLevel = useGlobalLevel ? globalLevel : (simLevels[mon.uuid] || (mon.is_god_roll ? 50 : mon.level));
    const isMinted = mintedMons.has(mon.uuid);
    const activeSubskills = seededSubskills[mon.uuid] || mon.subskills;
    const activeMainSkillLevel = seededMainSkills[mon.uuid] || mon.main_skill_level || 1;
    
    const simMon = { ...mon, level: effectiveLevel, nature: isMinted ? 'Serious' : mon.nature, subskills: activeSubskills, main_skill_level: activeMainSkillLevel };
    let species = pokedex[mon.species_id];
    
    let evoOptions = [];
    if (!mon.is_god_roll && useFinalEvolution) { 
        evoOptions = getEvolutionOptions(species, mon.nature);
        if (evoOptions.length > 0) {
            const userChoiceId = selectedEvos[mon.uuid];
            if (userChoiceId && evoOptions.some(o => o.id === userChoiceId)) {
                const target = pokedex[userChoiceId];
                if (target) species = target;
            } else {
                const defaultTarget = evoOptions.find(o => o.id === species.final_evolution) || evoOptions[0];
                if (defaultTarget) species = defaultTarget;
            }
        }
    } else if (mon.is_god_roll) {
        const baseSpecies = pokedex[mon.base_species_id] || species;
        if (baseSpecies) {
            const familyMembers = Object.values(pokedex).filter(p => p.family === baseSpecies.family);
            const finalStages = familyMembers.filter(p => p.final_evolution === p.id);
            if (finalStages.length > 1) {
                evoOptions = finalStages.sort((a, b) => a.name.localeCompare(b.name));
            }
        }
    }
    
    const prod = calculateProduction(species, simMon, duration, { energyStrategy, inventoryMode });
    
    const berryName = (species.berry || "Unknown").toUpperCase();
    const activeSkill = mon.custom_skill || species.skill;
    const mainSkillIcon = MAIN_SKILL_ICONS[activeSkill] || "/images/mainskill/metronome.png";
    const skillName = formatSkillName(activeSkill);
    const totalIng = Object.entries(prod.ingredients).reduce((acc, [name, count]) => {
        if (ingFilter.size > 0 && !ingFilter.has(name)) return acc;
        return acc + count;
    }, 0);

    // --- NEW: Generate growth curve data for the background graphs ---
    const growthData = [];
    const levelsToSim = [];
    // Stepping by 2 keeps calculation time perfectly lightweight
    for(let i=1; i<=100; i+=2) levelsToSim.push(i);
    // Force specific game milestones into the array to catch exact unlock jumps
    [10, 25, 30, 50, 60, 75, 100, effectiveLevel].forEach(ml => { if(!levelsToSim.includes(ml)) levelsToSim.push(ml) });
    levelsToSim.sort((a,b) => a - b);
    
    levelsToSim.forEach(lvl => {
        const testMon = { ...mon, level: lvl };
        const testProd = calculateProduction(species, testMon, duration, { energyStrategy, inventoryMode });
        const testIng = Object.entries(testProd.ingredients).reduce((acc, [name, count]) => {
            if (ingFilter.size > 0 && !ingFilter.has(name)) return acc;
            return acc + count;
        }, 0);
        growthData.push({
            lvl,
            berry: testProd.berries,
            ing: testIng,
            skill: parseFloat(testProd.skills)
        });
    });

    return { mon, species, prod, berryName, mainSkillIcon, skillName, totalIng, effectiveLevel, evoOptions, growthData, isMinted, activeSubskills, activeMainSkillLevel };
  });

  const maxBerries = Math.max(...rawData.map(d => d.prod.berries), 0);
  const maxIng = Math.max(...rawData.map(d => d.totalIng), 0);
  const maxSkill = Math.max(...rawData.map(d => parseFloat(d.prod.skills)), 0);

  // --- 5. RENDER HELPERS ---
  const getScore = (item) => {
    const { prod, totalIng } = item;
    if (gradingBasis === 'berry') return prod.berries === maxBerries ? 2 : 0;
    if (gradingBasis === 'ing') return totalIng === maxIng ? 2 : 0;
    if (gradingBasis === 'skill') return parseFloat(prod.skills) === maxSkill ? 2 : 0;
    let score = 0;
    if (prod.berries === maxBerries && maxBerries > 0) score++;
    if (totalIng === maxIng && maxIng > 0) score++;
    if (parseFloat(prod.skills) === maxSkill && maxSkill > 0) score++;
    return score;
  };

  const sortedData = [...rawData].sort((a, b) => {
    if (sortMethod === 'level') return b.effectiveLevel - a.effectiveLevel;
    if (sortMethod === 'berry') return b.prod.berries - a.prod.berries;
    if (sortMethod === 'ing') return b.totalIng - a.totalIng;
    if (sortMethod === 'skill') return parseFloat(b.prod.skills) - parseFloat(a.prod.skills);
    if (sortMethod === 'date') return (b.mon.date_met || "").localeCompare(a.mon.date_met || "");
    return 0;
  });

  const sortedByBerry = [...rawData].sort((a, b) => b.prod.berries - a.prod.berries);
  const sortedByIng = [...rawData].sort((a, b) => b.totalIng - a.totalIng);
  const sortedBySkill = [...rawData].sort((a, b) => parseFloat(b.prod.skills) - parseFloat(a.prod.skills));
  const sortedByVerdict = [...rawData].sort((a, b) => getScore(b) - getScore(a));

  const availableIngredientsSet = new Set(rawData.flatMap(item => Object.keys(item.prod.ingredients)));
  const displayedIngredients = Object.keys(INGREDIENT_ICONS).filter(ing => availableIngredientsSet.has(ing) || ingFilter.has(ing));

  const getWinnerBadge = (val, max, color) => {
    if (val > 0 && val === max) return <div className={`absolute top-2 right-2 ${color} text-white p-1 rounded-full shadow-sm z-20`}><Trophy size={10} /></div>;
    return null;
  };

  const getCellClass = (val, max, type, isGodRoll) => {
    const isFocus = gradingBasis === 'overall' || gradingBasis === type;
    const base = "flex flex-col items-center justify-center p-2 md:p-4 h-full border-r border-gray-200 transition-colors";
    
    if (isGodRoll) return `${base} bg-yellow-50/40`;
    if (!isFocus) return `${base} opacity-60`;
    
    const isWinner = val > 0 && val === max;
    if (isWinner) {
      if (type === 'berry') return `${base} bg-green-50/60`;
      if (type === 'ing') return `${base} bg-orange-50/60`;
      if (type === 'skill') return `${base} bg-blue-50/60`;
    }
    return base;
  };

  const renderActiveCard = (item, idx, isMobile = false) => {
    const { mon, species, effectiveLevel, evoOptions, mainSkillIcon, skillName, isMinted, activeSubskills, activeMainSkillLevel } = item;
    const imagePath = `/images/pokemon/${species.id.toLowerCase()}${mon.is_shiny ? '_shiny' : ''}.png`;
    const natureStats = NATURES[mon.nature];
    const hybrid = HYBRID_ROLES[species.id];
    const unlockLevels = [10, 25, 50, 75, 100];
    const hasHelpingBonus = mon.subskills.some((s, i) => s === 'Helping Bonus' && effectiveLevel >= unlockLevels[i]);

    const baseSkillLevel = mon.main_skill_level || 1;
    const isSkillSeeded = activeMainSkillLevel !== baseSkillLevel;
    const skillColorClass = isSkillSeeded ? 'bg-green-50 text-green-700 border-green-200' : 'bg-white text-gray-600 border-gray-200';
    const skillCursorClass = (!mon.is_god_roll && enableMintsSeeds) ? 'cursor-pointer ring-2 ring-teal-400 hover:bg-teal-50 hover:scale-105 transition-all z-10' : '';

    const headerClass = mon.is_god_roll 
        ? "bg-yellow-50 border-yellow-300 ring-1 ring-yellow-400 z-20"
        : "bg-white border-gray-200 z-10";

    const containerClasses = isMobile 
        ? `flex-shrink-0 w-48 p-3 flex flex-col items-center relative rounded-xl border shadow-sm ${headerClass}`
        : `sticky top-0 border-b border-r p-2 md:p-4 flex flex-col items-center relative overflow-hidden ${headerClass}`;

    return (
      <div key={mon.uuid || idx} className={containerClasses}>
        
        {!mon.is_god_roll && (
            <button 
                onClick={() => onRemove(mon.uuid)}
                className="absolute top-2 right-2 p-1.5 bg-gray-100 hover:bg-red-100 text-gray-400 hover:text-red-500 rounded-lg transition-colors z-20"
                title="Remove from comparison"
            >
                <X size={14} />
            </button>
        )}

        {mon.is_god_roll && (
            <div className="absolute top-2 right-2 text-yellow-500 animate-pulse z-20" title="Simulated perfect stats version">
                <Crown size={16} fill="currentColor" />
            </div>
        )}

        <img src={imagePath} className={`w-14 h-14 md:w-20 md:h-20 object-contain drop-shadow-md mb-2 ${mon.is_god_roll ? 'drop-shadow-xl scale-110' : ''}`} alt="" />
        <div className="text-center mb-1 w-full">
          <div className={`font-black text-sm md:text-base leading-tight flex flex-wrap items-center justify-center gap-1 w-full ${mon.is_god_roll ? 'text-yellow-700' : 'text-gray-800'}`}>
              <span className="truncate max-w-full">{mon.nickname || species.name}</span>
              {mon.is_shiny && <Sparkles size={12} className="text-indigo-600 fill-indigo-100 shrink-0" />}
          </div>
          
          {/* Evolution Dropdown */}
          {((!mon.is_god_roll && useFinalEvolution) || mon.is_god_roll) && (
              <>
                  {evoOptions.length > 1 ? (
                      <div className="my-1.5 flex justify-center w-full z-10 relative">
                          <div className="relative">
                            <select value={species.id} onChange={(e) => setSelectedEvos(prev => ({...prev, [mon.uuid]: e.target.value}))} className="appearance-none bg-teal-50 border border-teal-200 text-teal-700 text-[10px] font-black uppercase rounded-lg py-1 pl-2 pr-6 outline-none focus:ring-2 focus:ring-teal-300 cursor-pointer max-w-full truncate">
                                {evoOptions.map(opt => <option key={opt.id} value={opt.id}>{opt.name}</option>)}
                            </select>
                            <ChevronDown size={12} className="absolute right-1 top-1/2 -translate-y-1/2 text-teal-400 pointer-events-none" />
                          </div>
                      </div>
                  ) : (!mon.is_god_roll && mon.species_id !== species.id) ? (
                      <div className="my-1 flex justify-center w-full z-10 relative">
                          <span className="text-gray-400 text-[10px] font-bold">(Evolved)</span>
                      </div>
                  ) : null}
              </>
          )}

          {/* Nature Stats */}
          <div 
            className={`text-[10px] font-bold mt-0.5 uppercase tracking-wide flex flex-col sm:flex-row flex-wrap items-center justify-center gap-x-1 gap-y-0.5 leading-tight text-center px-1.5 py-0.5 rounded border transition-all ${isMinted ? 'bg-green-50 border-green-200 text-green-700 shadow-sm' : 'border-transparent text-gray-400'} ${enableMintsSeeds && !mon.is_god_roll ? 'cursor-pointer ring-2 ring-teal-400 hover:bg-teal-50 hover:scale-105' : ''}`}
            onClick={() => enableMintsSeeds && !mon.is_god_roll && toggleMint(mon.uuid)}
            title={mon.is_god_roll ? "Perfect stats" : (enableMintsSeeds ? "Click to simulate using a Mint (Neutral Nature)" : "")}
          >
            <span>{isMinted ? '🌿 Minted' : mon.nature}</span>
            {!isMinted && (natureStats.up || natureStats.down) && (
                <span className="flex flex-wrap justify-center gap-0.5 sm:ml-1">
                    {natureStats.up && <span className="text-red-500">+{natureStats.up}</span>}
                    {natureStats.down && <span className="text-blue-400">-{natureStats.down}</span>}
                </span>
            )}
          </div>

          {/* GOD ROLL: CLICKABLE INGREDIENTS */}
          <div className="flex gap-0.5 mt-2 bg-gray-50/80 p-1 rounded-lg border border-black/5 justify-center z-10 relative flex-wrap">
            {["0", "30", "60"].map((lvl) => {
                const ing = mon.ingredients[lvl];
                const isLocked = effectiveLevel < parseInt(lvl);
                const canCycle = mon.is_god_roll && species.ingredientPools[lvl].length > 1;

                return (
                    <div 
                        key={lvl} 
                        onClick={() => canCycle && cycleGodRollIngredient(mon.uuid, species, lvl)}
                        className={`relative w-6 h-6 flex items-center justify-center border rounded transition-all
                            ${isLocked ? 'opacity-30 grayscale border-transparent' : 'bg-white border-gray-200'}
                            ${canCycle ? 'cursor-pointer hover:border-teal-400 hover:shadow-sm hover:-translate-y-0.5' : ''}
                        `}
                        title={canCycle ? "Click to change ingredient" : ""}
                    >
                        <img src={INGREDIENT_ICONS[ing.name]} className="w-4 h-4 object-contain" alt=""/>
                        <span className="absolute -bottom-1 -right-1 text-[7px] font-black leading-none bg-white/90 px-0.5 rounded text-gray-500 border border-gray-100">
                            {ing.count}
                        </span>
                    </div>
                );
            })}
          </div>

          {/* BADGES ROW */}
          <div className="flex flex-wrap justify-center gap-1 mt-1.5 z-10 relative">
            {hasHelpingBonus && (
                <div className="bg-yellow-100 border border-yellow-200 text-yellow-700 px-2 py-1 rounded-md flex items-center gap-1.5 shadow-sm" title="Helping Bonus Active (+5% Speed)">
                    <HeartHandshake size={10} />
                    <span className="text-[9px] font-black uppercase tracking-tight">TEAM</span>
                </div>
            )}
            {hybrid && (
                <div className="bg-orange-50 border border-orange-200 text-orange-700 px-2 py-1 rounded-md flex items-center gap-1.5 shadow-sm" title={hybrid.desc}>
                    <AlertTriangle size={10} />
                    <span className="text-[9px] font-black uppercase tracking-tight">{hybrid.label}</span>
                </div>
            )}
          </div>

          {/* Date Met */}
          {!mon.is_god_roll && mon.date_met && (
              <div className="flex items-center justify-center gap-1.5 px-2 py-0.5 bg-gray-100/80 rounded-full text-[10px] font-black text-gray-600 mt-1 uppercase tracking-tight z-10 relative">
                  <Calendar size={10} className="text-gray-400" /> 
                  {new Date(mon.date_met).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: '2-digit' })}
              </div>
          )}
        </div>

        {/* Subskills */}
        <div className="flex flex-wrap justify-center gap-1 mt-2 mb-1 max-w-[160px] w-full z-10 relative">
            {activeSubskills.map((skill, i) => {
                if (!skill) return null;
                const baseSkill = mon.subskills[i];
                const isUpgraded = skill !== baseSkill;
                const unlockLevel = [10, 25, 50, 75, 100][i];
                const isUnlocked = effectiveLevel >= unlockLevel;
                const abbr = SUBSKILL_ABBREVIATIONS[skill] || skill.slice(0, 2);
                let colorClass = isUpgraded ? 'bg-green-50 text-green-700 border-green-200 shadow-sm' : getSkillColor(skill);
                
                if (mon.is_god_roll) colorClass += " ring-1 ring-yellow-400";

                const isUpgradeable = SUBSKILL_UPGRADES[baseSkill] !== undefined;
                const cursorClass = (isUnlocked && !mon.is_god_roll && isUpgradeable && enableMintsSeeds) ? 'cursor-pointer ring-2 ring-teal-400 hover:bg-teal-50 hover:scale-105 transition-all z-10' : '';
                
                const handleClick = () => {
                    if (!isUnlocked || mon.is_god_roll || !enableMintsSeeds) return;
                    handleSubskillTap(mon.uuid, i, baseSkill, activeSubskills);
                };

                return (
                    <div 
                        key={i} 
                        onClick={handleClick}
                        className={`text-[8px] font-black px-1 py-0.5 rounded border leading-none ${colorClass} ${!isUnlocked ? 'opacity-60 saturate-50' : ''} ${cursorClass}`} 
                        title={isUpgraded ? `Simulated Sub-skill Seed\nLv.${unlockLevel}: ${skill}` : `Lv.${unlockLevel}: ${skill}`}
                    >
                        {abbr}
                    </div>
                );
            })}
        </div>

        {/* Main Skill */}
        <div className="flex justify-center w-full z-10 relative mb-2">
            <div 
                className={`flex items-center gap-1 text-[8px] font-black px-1 py-0.5 rounded border leading-none shadow-sm ${skillColorClass} ${skillCursorClass} ${mon.is_god_roll ? 'ring-1 ring-yellow-400' : ''}`} 
                title={isSkillSeeded ? `Simulated Main Skill Seed\n${skillName}` : skillName}
                onClick={() => { if (!mon.is_god_roll && enableMintsSeeds) handleMainSkillTap(mon.uuid, activeMainSkillLevel, activeSubskills, effectiveLevel); }}
            >
                <img src={mainSkillIcon} className="w-4 h-4 object-contain" alt="" />
                <span>Lv. {activeMainSkillLevel}</span>
            </div>
        </div>

        {/* Sim Level */}
        <div className={`flex items-center gap-1 md:gap-2 px-1 md:px-2 py-1 rounded-lg border transition-colors z-10 relative mt-auto ${useGlobalLevel ? 'bg-gray-50 border-gray-100 opacity-50' : 'bg-gray-100 border-gray-200'}`}>
          <span className="text-[10px] font-bold text-gray-500 uppercase hidden md:block">Sim Lv.</span>
          <span className="text-[10px] font-bold text-gray-500 uppercase md:hidden">Lv.</span>
          <div className={`flex items-center rounded overflow-hidden h-6 md:h-auto ${useGlobalLevel ? 'bg-transparent' : 'bg-white border border-gray-300'}`}>
            <button disabled={useGlobalLevel} onClick={() => setSimLevels(prev => ({...prev, [mon.uuid]: Math.max(1, (parseInt(effectiveLevel) || 1) - 1)}))} className={`px-2 h-full bg-gray-50 hover:bg-gray-100 text-gray-600 font-bold border-r border-gray-300 flex items-center justify-center transition-colors md:hidden ${useGlobalLevel ? 'hidden' : ''}`}>-</button>
            <input type="number" min="1" max="100" disabled={useGlobalLevel} value={effectiveLevel} onChange={(e) => setSimLevels(prev => ({...prev, [mon.uuid]: e.target.value === '' ? '' : Math.min(100, parseInt(e.target.value) || 1) }))} onBlur={(e) => { if (!e.target.value || parseInt(e.target.value) < 1) setSimLevels(prev => ({...prev, [mon.uuid]: 1})); }} className={`w-8 md:w-10 h-full rounded-none text-center text-xs font-black outline-none focus:ring-1 focus:ring-teal-500 m-0 p-0 ${useGlobalLevel ? 'bg-transparent text-gray-400' : 'bg-white text-teal-600'}`} style={{ WebkitAppearance: 'none', MozAppearance: 'textfield' }}/>
            <button disabled={useGlobalLevel} onClick={() => setSimLevels(prev => ({...prev, [mon.uuid]: Math.min(100, (parseInt(effectiveLevel) || 1) + 1)}))} className={`px-2 h-full bg-gray-50 hover:bg-gray-100 text-gray-600 font-bold border-l border-gray-300 flex items-center justify-center transition-colors md:hidden ${useGlobalLevel ? 'hidden' : ''}`}>+</button>
          </div>
        </div>
      </div>
    );
  };

  // --- 6. MOBILE SECTIONS (Adaptive Ordering) ---
  const mobileBerrySection = (
    <div key="berry" className="border-b border-gray-200 bg-white">
      <button onClick={() => toggleMobileSection('berry')} className="w-full p-3 flex justify-between items-center font-black uppercase tracking-widest text-green-600 text-xs">
         <span>Berries</span>
         <ChevronDown className={`transition-transform ${expandedMobile.berry ? 'rotate-180' : ''}`} size={16} />
      </button>
      {expandedMobile.berry && (
         <div className="flex flex-col gap-2 p-3 bg-gray-50">
            {sortedByBerry.map((item, idx) => (
               <div key={idx} className={`relative p-3 rounded-xl border shadow-sm overflow-hidden ${item.mon.is_god_roll ? 'bg-yellow-50/40 border-yellow-200' : 'bg-white border-gray-200'}`}>
                  <BackgroundGraph growthData={item.growthData} type="berry" colorClass="text-green-500" currentLevel={item.effectiveLevel} />
                  <div className="relative z-10 flex items-center justify-between">
                     <div className="flex items-center gap-3">
                       <span className="font-black text-gray-300 text-lg w-4">{idx + 1}</span>
                       <img src={`/images/pokemon/${item.species.id.toLowerCase()}${item.mon.is_shiny ? '_shiny' : ''}.png`} className="w-10 h-10 object-contain drop-shadow-sm" alt="" />
                       <div className="flex flex-col">
                          <span className="font-bold text-sm text-gray-800 leading-none">{item.mon.nickname || item.species.name}</span>
                          <span className="text-[10px] text-gray-500 font-medium mt-0.5">Lv. {item.effectiveLevel}</span>
                       </div>
                     </div>
                     <div className="flex items-center gap-2">
                       <div className="flex flex-col items-end">
                         <span className="font-black text-xl text-gray-700 leading-none">{item.prod.berries}</span>
                         {maxBerries - item.prod.berries > 0 && <span className="text-[10px] font-bold text-red-400 mt-0.5">-{maxBerries - item.prod.berries}</span>}
                       </div>
                       <img src={BERRY_ICONS[item.berryName]} className="w-6 h-6 object-contain" alt="" />
                     </div>
                  </div>
               </div>
            ))}
         </div>
      )}
    </div>
  );

  const mobileIngSection = (
    <div key="ing" className="border-b border-gray-200 bg-white">
      <button onClick={() => toggleMobileSection('ing')} className="w-full p-3 flex justify-between items-center font-black uppercase tracking-widest text-orange-500 text-xs">
         <span>Ingredients</span>
         <ChevronDown className={`transition-transform ${expandedMobile.ing ? 'rotate-180' : ''}`} size={16} />
      </button>
      {expandedMobile.ing && (
         <div className="flex flex-col gap-2 p-3 bg-gray-50">
            {sortedByIng.map((item, idx) => (
               <div key={idx} className={`relative p-3 rounded-xl border shadow-sm overflow-hidden flex flex-col gap-2 ${item.mon.is_god_roll ? 'bg-yellow-50/40 border-yellow-200' : 'bg-white border-gray-200'}`}>
                  <BackgroundGraph growthData={item.growthData} type="ing" colorClass="text-orange-500" currentLevel={item.effectiveLevel} />
                  <div className="relative z-10 flex items-center justify-between">
                     <div className="flex items-center gap-3">
                       <span className="font-black text-gray-300 text-lg w-4">{idx + 1}</span>
                       <img src={`/images/pokemon/${item.species.id.toLowerCase()}${item.mon.is_shiny ? '_shiny' : ''}.png`} className="w-10 h-10 object-contain drop-shadow-sm" alt="" />
                       <div className="flex flex-col">
                          <span className="font-bold text-sm text-gray-800 leading-none">{item.mon.nickname || item.species.name}</span>
                          <span className="text-[10px] text-gray-500 font-medium mt-0.5">Lv. {item.effectiveLevel}</span>
                       </div>
                     </div>
                     <div className="flex items-center gap-2">
                       <div className="flex flex-col items-end">
                         <span className="font-black text-xl text-gray-700 leading-none">{item.totalIng.toFixed(0)}</span>
                         {maxIng - item.totalIng > 0.1 && <span className="text-[10px] font-bold text-red-400 mt-0.5">-{((maxIng - item.totalIng)).toFixed(1)}</span>}
                       </div>
                     </div>
                  </div>
                  <div className="relative z-10 flex flex-wrap gap-2 ml-7 pl-4">
                    {Object.entries(item.prod.ingredients).map(([ing, count]) => (
                        <div key={ing} className={`flex items-center gap-1 bg-white/80 backdrop-blur-[2px] border border-gray-200 px-1.5 py-0.5 rounded shadow-sm transition-opacity ${ingFilter.size === 0 || ingFilter.has(ing) ? 'opacity-100' : 'opacity-25 grayscale'}`} title={ing}>
                            <img src={INGREDIENT_ICONS[ing]} className="w-4 h-4" alt="" />
                            <span className="text-[10px] font-bold text-gray-600">{count.toFixed(1)}</span>
                        </div>
                    ))}
                  </div>
               </div>
            ))}
         </div>
      )}
    </div>
  );

  const mobileSkillSection = (
    <div key="skill" className="border-b border-gray-200 bg-white">
      <button onClick={() => toggleMobileSection('skill')} className="w-full p-3 flex justify-between items-center font-black uppercase tracking-widest text-blue-500 text-xs">
         <span>Skills</span>
         <ChevronDown className={`transition-transform ${expandedMobile.skill ? 'rotate-180' : ''}`} size={16} />
      </button>
      {expandedMobile.skill && (
         <div className="flex flex-col gap-2 p-3 bg-gray-50">
            {sortedBySkill.map((item, idx) => (
               <div key={idx} className={`relative p-3 rounded-xl border shadow-sm overflow-hidden flex flex-col gap-2 ${item.mon.is_god_roll ? 'bg-yellow-50/40 border-yellow-200' : 'bg-white border-gray-200'}`}>
                  <BackgroundGraph growthData={item.growthData} type="skill" colorClass="text-blue-500" currentLevel={item.effectiveLevel} />
                  <div className="relative z-10 flex items-center justify-between">
                     <div className="flex items-center gap-3">
                       <span className="font-black text-gray-300 text-lg w-4">{idx + 1}</span>
                       <img src={`/images/pokemon/${item.species.id.toLowerCase()}${item.mon.is_shiny ? '_shiny' : ''}.png`} className="w-10 h-10 object-contain drop-shadow-sm" alt="" />
                       <div className="flex flex-col">
                          <span className="font-bold text-sm text-gray-800 leading-none">{item.mon.nickname || item.species.name}</span>
                          <span className="text-[10px] text-gray-500 font-medium mt-0.5">Lv. {item.effectiveLevel}</span>
                       </div>
                     </div>
                     <div className="flex items-center gap-2">
                       <div className="flex flex-col items-end">
                         <span className="font-black text-xl text-gray-700 leading-none">{item.prod.skills}</span>
                         {maxSkill - parseFloat(item.prod.skills) > 0.01 && <span className="text-[10px] font-bold text-red-400 mt-0.5">-{((maxSkill - parseFloat(item.prod.skills))).toFixed(2)}</span>}
                       </div>
                       <img src={item.mainSkillIcon} className="w-6 h-6 object-contain" alt="" />
                     </div>
                  </div>
               </div>
            ))}
         </div>
      )}
    </div>
  );

  const sortedMobileSections = [
    { id: 'berry', node: mobileBerrySection },
    { id: 'ing', node: mobileIngSection },
    { id: 'skill', node: mobileSkillSection }
  ].sort((a, b) => a.id === sortMethod ? -1 : (b.id === sortMethod ? 1 : 0));

  return (
    <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-200" onClick={onClose}>
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-[98vw] h-[95vh] md:h-[92vh] flex flex-col overflow-hidden ring-1 ring-gray-900/5 relative" onClick={(e) => e.stopPropagation()}>
        
        {/* DARK HEADER */}
        <div className="bg-gray-800 p-3 md:p-4 flex justify-between items-center text-white shrink-0 shadow-md z-40">
          <div className="flex items-center gap-2">
            <Trophy size={20} className="text-teal-400" />
            <h2 className="text-lg md:text-xl font-black tracking-tight">Compare</h2>
          </div>
          <button onClick={onClose} className="p-1.5 md:p-2 bg-gray-700 hover:bg-red-500 rounded-lg transition-colors text-gray-300 hover:text-white shadow-sm" aria-label="Close modal">
            <X size={20} />
          </button>
        </div>

        {/* DESKTOP SETTINGS BAR */}
        <div className="hidden md:flex md:static md:w-full md:max-w-none md:bg-gray-50 md:p-3 md:border-x-0 md:border-t-0 md:border-b md:border-gray-200 md:rounded-none md:shadow-sm md:z-30 flex-wrap items-center gap-3 md:gap-4 relative" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm shrink-0">
                <button onClick={() => setInventoryMode('unlimited')} className={`px-3 py-1.5 flex items-center gap-1.5 text-xs font-bold transition-colors ${inventoryMode === 'unlimited' ? 'bg-teal-600 text-white' : 'text-gray-500 hover:bg-gray-50'}`} title="Active Play">
                    <Sun size={14} /> Active
                </button>
                <div className="w-px h-4 bg-gray-200"></div>
                <button onClick={() => setInventoryMode('capped')} className={`px-3 py-1.5 flex items-center gap-1.5 text-xs font-bold transition-colors ${inventoryMode === 'capped' ? 'bg-teal-900 text-white' : 'text-gray-500 hover:bg-gray-50'}`} title="Overnight">
                    <Moon size={14} /> Sleep
                </button>
            </div>

            <select value={energyStrategy} onChange={(e) => setEnergyStrategy(e.target.value)} className="bg-white border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-bold outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-gray-700 shadow-sm cursor-pointer shrink-0">
                <option value="maintained">High Energy (E4E)</option>
                <option value="natural">Natural Decay</option>
                <option value="zero">Zero Energy</option>
            </select>

            <div className="flex items-center gap-2 bg-white px-2 py-1.5 rounded-lg border border-gray-200 shadow-sm shrink-0">
                <Clock size={14} className="text-teal-400" />
                <span className="text-xs font-bold text-gray-700 w-6">{duration}h</span>
                <input type="range" min="4" max="24" step="1" value={duration} onChange={(e) => setDuration(parseInt(e.target.value))} className="w-16 md:w-20 accent-teal-500 cursor-pointer"/>
            </div>

            <div className="flex items-center gap-2 bg-white px-2 py-1.5 rounded-lg border border-gray-200 shadow-sm shrink-0">
                <ArrowUpDown size={14} className="text-teal-400" />
                <select value={sortMethod} onChange={(e) => setSortMethod(e.target.value)} className="bg-transparent font-bold text-xs outline-none focus:ring-2 focus:ring-teal-500 rounded text-gray-700 cursor-pointer">
                    <option value="level">Sort: Level</option>
                    <option value="berry">Sort: Berries</option>
                    <option value="ing">Sort: Ingredients</option>
                    <option value="skill">Sort: Skill</option>
                </select>
            </div>

            <div className="flex-grow hidden md:block"></div>

            <div className="flex flex-wrap items-center gap-2 md:gap-4 w-full md:w-auto mt-1 md:mt-0">
                <label className="flex items-center gap-1.5 cursor-pointer hover:bg-gray-200 p-1 rounded transition" title="Generate Perfect Stats to see maximum potential">
                  <input type="checkbox" className="w-4 h-4 text-teal-600 accent-teal-600 rounded border-gray-300 focus:ring-teal-500" checked={showGodRolls} onChange={(e) => setShowGodRolls(e.target.checked)} />
                  <span className="text-xs font-bold text-gray-700 flex items-center gap-1"><Crown size={14} className="text-teal-500" /> Perfect Stats</span>
                </label>

                <label className="flex items-center gap-1.5 cursor-pointer hover:bg-gray-200 p-1 rounded transition" title="Simulate all selected Pokémon as their final evolution">
                  <input type="checkbox" className="w-4 h-4 text-teal-600 accent-teal-600 rounded border-gray-300 focus:ring-teal-500" checked={useFinalEvolution} onChange={(e) => setUseFinalEvolution(e.target.checked)} />
                  <span className="text-xs font-bold text-gray-700 flex items-center gap-1"><ArrowUpCircle size={14} className="text-teal-500" /> Evolved</span>
                </label>

                <label className="flex items-center gap-1.5 cursor-pointer hover:bg-gray-200 p-1 rounded transition" title="Enable simulating Mints & Seeds by tapping stats">
                  <input type="checkbox" className="w-4 h-4 text-teal-600 accent-teal-600 rounded border-gray-300 focus:ring-teal-500" checked={enableMintsSeeds} onChange={(e) => setEnableMintsSeeds(e.target.checked)} />
                  <span className="text-xs font-bold text-gray-700 flex items-center gap-1"><Sparkles size={14} className="text-teal-500" /> Mints & Seeds</span>
                </label>

                <div className="flex items-center gap-1.5 hover:bg-gray-200 p-1 rounded transition" title="Scale all Pokémon to the same level for a fair comparison">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" className="w-4 h-4 text-teal-600 accent-teal-600 rounded border-gray-300 focus:ring-teal-500" checked={useGlobalLevel} onChange={(e) => setUseGlobalLevel(e.target.checked)} />
                    <span className="text-xs font-bold text-gray-700 flex items-center gap-1"><SlidersHorizontal size={14} className="text-teal-500" /> Sync</span>
                  </label>
                  {useGlobalLevel && (
                    <div className="flex items-center gap-2 ml-1">
                        <div className="flex items-center gap-0 bg-white border border-gray-300 rounded overflow-hidden shadow-sm h-6">
                            <button onClick={() => { if (snapGlobalLevel) { const levels = [10, 25, 30, 50, 60, 75, 100]; setGlobalLevel([...levels].reverse().find(l => l < globalLevel) || levels[0]); } else { setGlobalLevel(Math.max(1, (globalLevel || 1) - 1)); } }} className="px-2 h-full bg-gray-50 hover:bg-gray-100 text-gray-600 font-bold border-r border-gray-300 flex items-center justify-center transition-colors" title="Decrease Level">-</button>
                            <input type="number" min="1" max="100" value={globalLevel} readOnly={snapGlobalLevel} onChange={(e) => setGlobalLevel(e.target.value === '' ? '' : Math.min(100, parseInt(e.target.value) || 1))} onBlur={() => { if (!globalLevel || globalLevel < 1) setGlobalLevel(1); }} className={`w-8 h-full text-center text-xs font-black text-teal-600 outline-none ${snapGlobalLevel ? 'bg-gray-50' : ''}`} style={{ WebkitAppearance: 'none', MozAppearance: 'textfield' }} />
                            <button onClick={() => { if (snapGlobalLevel) { const levels = [10, 25, 30, 50, 60, 75, 100]; setGlobalLevel(levels.find(l => l > globalLevel) || levels[levels.length - 1]); } else { setGlobalLevel(Math.min(100, (globalLevel || 1) + 1)); } }} className="px-2 h-full bg-gray-50 hover:bg-gray-100 text-gray-600 font-bold border-l border-gray-300 flex items-center justify-center transition-colors" title="Increase Level">+</button>
                        </div>
                        <label className="flex items-center gap-1 cursor-pointer" title="Only allow common unlock levels">
                            <input type="checkbox" className="w-3 h-3 text-teal-600 accent-teal-600 rounded border-gray-300 focus:ring-teal-500" checked={snapGlobalLevel} onChange={(e) => {
                                setSnapGlobalLevel(e.target.checked);
                                if (e.target.checked && ![10, 25, 30, 50, 60, 75, 100].includes(globalLevel)) {
                                    const levels = [10, 25, 30, 50, 60, 75, 100];
                                    const nearest = levels.reduce((prev, curr) => Math.abs(curr - globalLevel) < Math.abs(prev - globalLevel) ? curr : prev);
                                    setGlobalLevel(nearest);
                                }
                            }} />
                            <span className="text-[10px] font-bold text-gray-500 leading-none">Snap</span>
                        </label>
                    </div>
                  )}
                </div>

                <div className="relative shrink-0 ml-1" ref={filterRef}>
                    <button onClick={() => setIsIngFilterOpen(!isIngFilterOpen)} className={`p-1.5 rounded-lg border transition-colors flex items-center gap-1 text-xs font-bold shadow-sm ${ingFilter.size > 0 ? 'bg-orange-100 border-orange-300 text-orange-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                        <Filter size={14} /> Filter Ing
                    </button>
                    {isIngFilterOpen && (
                        <>
                        <div className="fixed inset-0 z-40 md:hidden bg-black/20 backdrop-blur-[2px]" onClick={(e) => { e.stopPropagation(); setIsIngFilterOpen(false); }}></div>
                        <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 md:absolute md:top-full md:mt-2 md:right-0 md:left-auto md:translate-x-0 md:translate-y-0 w-72 bg-white rounded-xl shadow-2xl md:shadow-xl border border-gray-200 p-4 z-50 grid grid-cols-4 gap-2">
                            {displayedIngredients.length > 0 ? (
                                displayedIngredients.map(ing => (
                                    <button key={ing} onClick={() => toggleIngFilter(ing)} className={`p-2 rounded-lg border flex items-center justify-center transition-colors ${ingFilter.has(ing) ? 'bg-orange-50 border-orange-200 shadow-inner' : 'border-transparent hover:bg-gray-50'}`}>
                                        <img src={INGREDIENT_ICONS[ing]} className="w-8 h-8 object-contain" alt={ing} />
                                    </button>
                                ))
                            ) : (
                                <div className="col-span-4 text-center text-xs font-bold text-gray-400 py-2">No ingredients produced</div>
                            )}
                        </div>
                        </>
                    )}
                </div>
            </div>
            </div>

        {/* Scrollable Content */}
        {/* DESKTOP LAYOUT */}
        <div className="hidden md:flex flex-grow overflow-auto bg-gray-50/50">
          <div className="min-w-max">
            <div className="grid" style={{ gridTemplateColumns: `clamp(100px, 25vw, 180px) repeat(${sortedData.length}, minmax(180px, 1fr))` }}>
              
              {/* HEADER ROW */}
              <div className="sticky left-0 top-0 z-50 bg-white border-b border-r border-gray-200 p-2 md:p-4 font-black text-[10px] md:text-xs text-gray-400 uppercase tracking-widest flex items-end pb-6 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                Active Pokémon
              </div>
              
              {sortedData.map((item, idx) => renderActiveCard(item, idx, false))}

              {/* BERRY ROW */}
              <div className="sticky left-0 bg-white border-r border-b border-gray-200 p-2 md:p-4 flex flex-col justify-center z-40 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                <span className={`text-[10px] md:text-xs font-black uppercase tracking-widest mb-1 ${gradingBasis === 'berry' ? 'text-green-600' : 'text-gray-400'}`}>Berries</span>
              </div>
              {sortedData.map(({ prod, berryName, mon, effectiveLevel, growthData }, idx) => {
                const delta = maxBerries - prod.berries;
                const percent = maxBerries > 0 ? Math.round((delta / maxBerries) * 100) : 0;
                
                return (
                <div key={idx} className={`${getCellClass(prod.berries, maxBerries, 'berry', mon.is_god_roll)} border-b relative overflow-hidden`}>
                  <BackgroundGraph growthData={growthData} type="berry" colorClass="text-green-500" currentLevel={effectiveLevel} />
                  
                  {gradingBasis === 'berry' && getWinnerBadge(prod.berries, maxBerries, 'bg-green-500')}
                  
                  <div className="flex items-center gap-3 mb-2 relative z-0">
                    <img src={BERRY_ICONS[berryName]} className="w-8 h-8 drop-shadow-sm" alt="" />
                    <div className="flex flex-col">
                        <span className="text-3xl font-black text-gray-700 leading-none">{prod.berries}</span>
                        {delta > 0 && (
                            <span className="text-[10px] font-bold text-red-400 mt-1">-{delta} (-{percent}%)</span>
                        )}
                    </div>
                  </div>
                  <div className="mt-auto w-full relative z-0">
                    <div className="w-full bg-gray-100/70 backdrop-blur-[2px] h-1.5 rounded-full overflow-hidden">
                      <div className="h-full bg-green-400 rounded-full" style={{ width: `${(prod.berries / maxBerries) * 100}%` }}></div>
                    </div>
                  </div>
                </div>
              )})}

              {/* INGREDIENT ROW */}
              <div className="sticky left-0 bg-white border-r border-b border-gray-200 p-2 md:p-4 flex flex-col justify-center z-40 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                <span className={`text-[10px] md:text-xs font-black uppercase tracking-widest mb-1 ${gradingBasis === 'ing' ? 'text-orange-500' : 'text-gray-400'}`}>Ingredients</span>
              </div>
              {sortedData.map(({ totalIng, prod, mon, effectiveLevel, growthData }, idx) => {
                const delta = maxIng - totalIng;
                const percent = maxIng > 0 ? Math.round((delta / maxIng) * 100) : 0;

                return (
                <div key={idx} className={`${getCellClass(totalIng, maxIng, 'ing', mon.is_god_roll)} border-b relative overflow-hidden`}>
                  <BackgroundGraph growthData={growthData} type="ing" colorClass="text-orange-500" currentLevel={effectiveLevel} />
                  
                  {gradingBasis === 'ing' && getWinnerBadge(totalIng, maxIng, 'bg-orange-500')}
                  
                  <div className="flex flex-col items-center mb-2 relative z-0">
                      <span className="text-3xl font-black text-gray-700 leading-none">{totalIng.toFixed(0)}</span>
                      {delta > 0.1 && (
                          <span className="text-[10px] font-bold text-red-400 mt-1">-{delta.toFixed(1)} (-{percent}%)</span>
                      )}
                  </div>

                  <div className="flex flex-wrap justify-center gap-2 mt-auto pt-2 relative z-0">
                    {Object.entries(prod.ingredients).map(([ing, count]) => (
                        <div key={ing} className={`flex items-center gap-1.5 bg-white/80 backdrop-blur-[2px] border border-gray-200 px-2 py-1 rounded-lg shadow-sm transition-opacity ${ingFilter.size === 0 || ingFilter.has(ing) ? 'opacity-100' : 'opacity-25 grayscale'}`} title={ing}>
                            <img src={INGREDIENT_ICONS[ing]} className="w-5 h-5" alt="" />
                            <span className="text-xs font-bold text-gray-600">{count.toFixed(1)}</span>
                        </div>
                    ))}
                  </div>
                </div>
              )})}

              {/* SKILL ROW */}
              <div className="sticky left-0 bg-white border-r border-b border-gray-200 p-2 md:p-4 flex flex-col justify-center z-40 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                <span className={`text-[10px] md:text-xs font-black uppercase tracking-widest mb-1 ${gradingBasis === 'skill' ? 'text-blue-500' : 'text-gray-400'}`}>Skill</span>
              </div>
              {sortedData.map(({ prod, mainSkillIcon, skillName, mon, effectiveLevel, growthData }, idx) => {
                const val = parseFloat(prod.skills);
                const delta = maxSkill - val;
                const percent = maxSkill > 0 ? Math.round((delta / maxSkill) * 100) : 0;

                return (
                <div key={idx} className={`${getCellClass(val, maxSkill, 'skill', mon.is_god_roll)} border-b relative overflow-hidden`}>
                  <BackgroundGraph growthData={growthData} type="skill" colorClass="text-blue-500" currentLevel={effectiveLevel} />
                  
                  {gradingBasis === 'skill' && getWinnerBadge(val, maxSkill, 'bg-blue-500')}
                  
                  <div className="flex flex-col items-center relative z-0" title={skillName}>
                    <img src={mainSkillIcon} className="w-10 h-10 object-contain mb-1 drop-shadow-sm" alt="" />
                    <span className="text-3xl font-black text-gray-700 leading-none">{prod.skills}</span>
                    {delta > 0.01 && (
                        <span className="text-[10px] font-bold text-red-400 mt-1">-{delta.toFixed(2)} (-{percent}%)</span>
                    )}
                  </div>

                  <div className="mt-auto pt-3 w-full relative z-0">
                    <div className="w-full bg-gray-100/70 backdrop-blur-[2px] h-1.5 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-400 rounded-full" style={{ width: `${(val / maxSkill) * 100}%` }}></div>
                    </div>
                  </div>
                </div>
              )})}

              {/* FOOTER: VERDICT */}
              <div className="sticky left-0 bg-white border-r border-gray-200 p-2 md:p-4 text-[10px] md:text-xs font-black text-gray-300 uppercase tracking-widest flex items-center z-40 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                Verdict
              </div>
              {sortedData.map((item, idx) => {
                 const score = getScore(item);
                 const isWinner = gradingBasis === 'overall' ? score >= 2 : score === 2;
                 return (
                    <div key={idx} className={`p-2 md:p-6 border-r border-gray-200 flex flex-col justify-center items-center gap-2 ${item.mon.is_god_roll ? 'bg-yellow-50/40' : 'bg-white'}`}>
                        {isWinner ? (
                            <div className="flex items-center gap-2 text-teal-700 bg-teal-50 px-4 py-2 rounded-xl font-black text-sm border border-teal-200 shadow-sm animate-bounce-in">
                                <Star size={16} className="fill-teal-600" /> {gradingBasis === 'overall' ? 'Top Pick' : 'Winner'}
                            </div>
                        ) : score > 0 ? (
                            <div className="flex items-center gap-2 text-gray-500 bg-gray-50 px-3 py-1.5 rounded-xl font-bold text-xs">
                                <TrendingUp size={14} /> Viable
                            </div>
                        ) : <span className="text-gray-300 text-xs font-bold uppercase">-</span>}

                        {item.prod.inventoryFullTime && (
                            <div className="flex items-center gap-1 text-[10px] font-black text-red-500 bg-red-50 px-2 py-1 rounded border border-red-100 animate-pulse">
                                <Briefcase size={10} /> Full @ {item.prod.inventoryFullTime}h
                            </div>
                        )}
                    </div>
                 );
              })}

            </div>
          </div>
        </div>
        
        {/* MOBILE LAYOUT */}
        <div className="md:hidden flex-grow flex flex-col overflow-y-auto bg-gray-50 relative pb-8">
            {/* STICKY TOP HEADER: Small Portraits */}
            <div className="sticky top-0 z-50 bg-white border-b border-gray-200 p-2 shadow-md flex items-center justify-between gap-3 overflow-x-auto no-scrollbar">
              <div className="flex items-center gap-3 flex-nowrap shrink-0">
                {sortedData.map((item, idx) => (
                  <div key={idx} className="relative shrink-0">
                    {!item.mon.is_god_roll && (
                        <button onClick={() => onRemove(item.mon.uuid)} className="absolute -top-1 -right-1 p-0.5 bg-white text-gray-400 rounded-full shadow-sm z-20"><X size={10}/></button>
                    )}
                    <img src={`/images/pokemon/${item.species.id.toLowerCase()}${item.mon.is_shiny ? '_shiny' : ''}.png`} className={`w-10 h-10 object-contain drop-shadow-sm ${item.mon.is_god_roll ? 'ring-2 ring-yellow-400 bg-yellow-50 rounded-full' : ''}`} alt="" />
                  </div>
                ))}
              </div>
              <div className="shrink-0 pr-1">
                <select value={sortMethod} onChange={(e) => {
                    setSortMethod(e.target.value);
                    const secMap = { 'berry': 'berry', 'ing': 'ing', 'skill': 'skill', 'level': 'verdict' };
                    setExpandedMobile(prev => ({ ...prev, [secMap[e.target.value] || 'verdict']: true }));
                }} className="bg-teal-50 border border-teal-200 text-teal-700 text-[10px] font-black uppercase rounded-lg py-1.5 px-2 outline-none focus:ring-2 focus:ring-teal-300 cursor-pointer shadow-sm">
                    <option value="level">Lvl</option>
                    <option value="berry">Berries</option>
                    <option value="ing">Ings</option>
                    <option value="skill">Skills</option>
                </select>
              </div>
            </div>
          
            {/* EXPANDABLE SECTIONS */}
            
            {/* SETTINGS & POKEMON */}
            <div className="border-b border-gray-200 bg-white">
              <button onClick={() => toggleMobileSection('active')} className="w-full p-3 flex justify-between items-center font-black uppercase tracking-widest text-teal-600 text-xs bg-teal-50/50">
                 <span>Settings & Pokémon</span>
                 <ChevronDown className={`transition-transform ${expandedMobile.active ? 'rotate-180' : ''}`} size={16} />
              </button>
              {expandedMobile.active && (
                 <div className="flex flex-col bg-gray-50">
                    <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-3 border-b border-gray-200">
                        <div className="flex items-center bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                            <button onClick={() => setInventoryMode('unlimited')} className={`flex-1 px-3 py-1.5 flex justify-center items-center gap-1.5 text-xs font-bold transition-colors ${inventoryMode === 'unlimited' ? 'bg-teal-600 text-white' : 'text-gray-500 hover:bg-gray-50'}`} title="Active Play"><Sun size={14} /> Active</button>
                            <div className="w-px h-4 bg-gray-200"></div>
                            <button onClick={() => setInventoryMode('capped')} className={`flex-1 px-3 py-1.5 flex justify-center items-center gap-1.5 text-xs font-bold transition-colors ${inventoryMode === 'capped' ? 'bg-teal-900 text-white' : 'text-gray-500 hover:bg-gray-50'}`} title="Overnight"><Moon size={14} /> Sleep</button>
                        </div>
                        <select value={energyStrategy} onChange={(e) => setEnergyStrategy(e.target.value)} className="bg-white border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-bold outline-none focus:ring-2 focus:ring-teal-500 text-gray-700 shadow-sm cursor-pointer w-full">
                            <option value="maintained">High Energy (E4E)</option>
                            <option value="natural">Natural Decay</option>
                            <option value="zero">Zero Energy</option>
                        </select>
                        <div className="flex items-center justify-between gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm">
                            <div className="flex items-center gap-1.5"><Clock size={14} className="text-teal-400" /><span className="text-xs font-bold text-gray-700 w-6">{duration}h</span></div>
                            <input type="range" min="4" max="24" step="1" value={duration} onChange={(e) => setDuration(parseInt(e.target.value))} className="w-full accent-teal-500 cursor-pointer"/>
                        </div>
                        <div className="flex flex-wrap items-center gap-3 bg-white px-3 py-2 rounded-lg border border-gray-200 shadow-sm">
                            <label className="flex items-center gap-1.5 cursor-pointer">
                              <input type="checkbox" className="w-4 h-4 text-teal-600 accent-teal-600 rounded border-gray-300 focus:ring-teal-500" checked={showGodRolls} onChange={(e) => setShowGodRolls(e.target.checked)} />
                              <span className="text-xs font-bold text-gray-700"><Crown size={14} className="text-teal-500 inline mr-0.5" /> Perfect Stats</span>
                            </label>
                            <label className="flex items-center gap-1.5 cursor-pointer">
                              <input type="checkbox" className="w-4 h-4 text-teal-600 accent-teal-600 rounded border-gray-300 focus:ring-teal-500" checked={useFinalEvolution} onChange={(e) => setUseFinalEvolution(e.target.checked)} />
                              <span className="text-xs font-bold text-gray-700"><ArrowUpCircle size={14} className="text-teal-500 inline mr-0.5" /> Evolved</span>
                            </label>
                            <label className="flex items-center gap-1.5 cursor-pointer">
                              <input type="checkbox" className="w-4 h-4 text-teal-600 accent-teal-600 rounded border-gray-300 focus:ring-teal-500" checked={enableMintsSeeds} onChange={(e) => setEnableMintsSeeds(e.target.checked)} />
                              <span className="text-xs font-bold text-gray-700"><Sparkles size={14} className="text-teal-500 inline mr-0.5" /> Mints/Seeds</span>
                            </label>
                            <div className="flex items-center gap-2">
                              <label className="flex items-center gap-1.5 cursor-pointer">
                                <input type="checkbox" className="w-4 h-4 text-teal-600 accent-teal-600 rounded border-gray-300 focus:ring-teal-500" checked={useGlobalLevel} onChange={(e) => setUseGlobalLevel(e.target.checked)} />
                                <span className="text-xs font-bold text-gray-700"><SlidersHorizontal size={14} className="text-teal-500 inline mr-0.5" /> Sync Lvl</span>
                              </label>
                              {useGlobalLevel && (
                                <div className="flex items-center bg-gray-50 border border-gray-300 rounded overflow-hidden shadow-sm h-6">
                                    <button onClick={() => { if (snapGlobalLevel) { const levels = [10, 25, 30, 50, 60, 75, 100]; setGlobalLevel([...levels].reverse().find(l => l < globalLevel) || levels[0]); } else { setGlobalLevel(Math.max(1, (globalLevel || 1) - 1)); } }} className="px-2 h-full bg-gray-100 hover:bg-gray-200 text-gray-600 font-bold border-r border-gray-300 transition-colors">-</button>
                                    <input type="number" min="1" max="100" value={globalLevel} readOnly={snapGlobalLevel} onChange={(e) => setGlobalLevel(e.target.value === '' ? '' : Math.min(100, parseInt(e.target.value) || 1))} className={`w-8 h-full text-center text-xs font-black text-teal-600 outline-none ${snapGlobalLevel ? 'bg-gray-100' : 'bg-white'}`} style={{ WebkitAppearance: 'none', MozAppearance: 'textfield' }} />
                                    <button onClick={() => { if (snapGlobalLevel) { const levels = [10, 25, 30, 50, 60, 75, 100]; setGlobalLevel(levels.find(l => l > globalLevel) || levels[levels.length - 1]); } else { setGlobalLevel(Math.min(100, (globalLevel || 1) + 1)); } }} className="px-2 h-full bg-gray-100 hover:bg-gray-200 text-gray-600 font-bold border-l border-gray-300 transition-colors">+</button>
                                </div>
                              )}
                            </div>
                        </div>
                    </div>
                    <div className="flex overflow-x-auto gap-3 p-3 no-scrollbar">
                       {sortedData.map((item, idx) => renderActiveCard(item, idx, true))}
                    </div>
                 </div>
              )}
            </div>
          
            {/* DYNAMIC SECTIONS */}
            {sortedMobileSections.map(s => s.node)}
          
            {/* VERDICT */}
            <div className="bg-white pb-8">
              <button onClick={() => toggleMobileSection('verdict')} className="w-full p-3 flex justify-between items-center font-black uppercase tracking-widest text-gray-400 text-xs bg-gray-100/50">
                 <span>Verdict</span>
                 <ChevronDown className={`transition-transform ${expandedMobile.verdict ? 'rotate-180' : ''}`} size={16} />
              </button>
              {expandedMobile.verdict && (
                 <div className="flex flex-col gap-2 p-3 bg-gray-50">
                    {sortedByVerdict.map((item, idx) => {
                       const score = getScore(item);
                       const isWinner = gradingBasis === 'overall' ? score >= 2 : score === 2;
                       return (
                           <div key={idx} className={`p-3 rounded-xl border shadow-sm flex items-center justify-between ${item.mon.is_god_roll ? 'bg-yellow-50/40 border-yellow-200' : 'bg-white border-gray-200'}`}>
                              <div className="flex items-center gap-3">
                               <span className="font-black text-gray-300 text-lg w-4">{idx + 1}</span>
                               <img src={`/images/pokemon/${item.species.id.toLowerCase()}${item.mon.is_shiny ? '_shiny' : ''}.png`} className="w-8 h-8 object-contain drop-shadow-sm" alt="" />
                               <div className="flex flex-col">
                                  <span className="font-bold text-sm text-gray-800 leading-none">{item.mon.nickname || item.species.name}</span>
                               </div>
                              </div>
                              <div className="flex flex-col items-end gap-1">
                                  {isWinner ? (
                                      <div className="flex items-center gap-1.5 text-teal-700 bg-teal-50 px-2 py-1 rounded-lg font-black text-xs border border-teal-200">
                                          <Star size={12} className="fill-teal-600" /> {gradingBasis === 'overall' ? 'Top Pick' : 'Winner'}
                                      </div>
                                  ) : score > 0 ? (
                                      <div className="flex items-center gap-1.5 text-gray-500 bg-gray-50 px-2 py-1 rounded-lg font-bold text-xs border border-gray-200">
                                          <TrendingUp size={12} /> Viable
                                      </div>
                                  ) : <span className="text-gray-300 text-xs font-bold uppercase">-</span>}
                                  {item.prod.inventoryFullTime && (
                                      <div className="flex items-center gap-1 text-[9px] font-black text-red-500 bg-red-50 px-1.5 py-0.5 rounded border border-red-100">
                                          <Briefcase size={8} /> Full @ {item.prod.inventoryFullTime}h
                                      </div>
                                  )}
                              </div>
                           </div>
                       );
                    })}
                 </div>
              )}
            </div>
        </div>
      </div>
    </div>
  );
}